import * as THREE from 'three';

// Decal factory — livery as DATA, painted at runtime (the decal game).
//
// Three registers, per the design doctrine (docs/SHIP-RENDERING-ARCHITECTURE
// transfer plan + the study's typography lesson):
//  - BANNER: naval-scale typography. House marks + registry numbers sized to
//    be read by another ship's optics, not by the player's UI. Stacked
//    top-to-bottom (upright letters) or horizontal.
//  - HAZARD: warning striping that traces FUNCTION — docking collars,
//    boarding bays, airlock surrounds. Accent color follows load paths and
//    operational zones, never decoration.
//  - Canvas-drawn (text is data; diffusion makes gibberish), with paint-wear
//    speckle so marks read as painted-on, then worn by service.
//
// Curved placement: curvedDecal() returns a partial-arc cylinder shell that
// hugs tankage/collar geometry (flat planes float off curved hulls).

export function bannerTexture(text: string, opts: {
  color?: string; vertical?: boolean; worn?: number; px?: number;
} = {}): THREE.CanvasTexture {
  const { color = '#dfe6f0', vertical = false, worn = 0.35, px = 160 } = opts;
  const chars = [...text];
  const c = document.createElement('canvas');
  if (vertical) {
    c.width = px * 1.4;
    c.height = px * 1.15 * chars.length;
  } else {
    c.width = Math.max(px * 0.72 * chars.length, px);
    c.height = px * 1.5;
  }
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${px}px "Arial Narrow", "Helvetica Neue", Arial, sans-serif`;
  if (vertical) {
    chars.forEach((ch, i) => ctx.fillText(ch, c.width / 2, px * 1.15 * (i + 0.55)));
  } else {
    ctx.fillText(text, c.width / 2, c.height / 2 + px * 0.06);
  }
  // paint wear: speckle erosion so the mark reads painted-then-worn
  if (worn > 0) {
    ctx.globalCompositeOperation = 'destination-out';
    const n = Math.floor((c.width * c.height) / 900 * worn);
    for (let i = 0; i < n; i++) {
      const r = 1 + Math.random() * 3.2;
      ctx.globalAlpha = 0.25 + Math.random() * 0.75;
      ctx.beginPath();
      ctx.arc(Math.random() * c.width, Math.random() * c.height, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

type LineOpts = { turn: 1 | -1; style: 'brush' | 'stencil'; color?: string; px?: number };
const _lines = new Map<string, THREE.CanvasTexture>();
/** One word of a hull line, 'brush' or 'stencil'. `turn` -1 runs it up the canvas,
 *  1 down, which is what reads left to right on the starboard and port flank shells. */
export function lineTexture(text: string, opts: LineOpts): THREE.CanvasTexture {
  const key = `${text}|${opts.turn}|${opts.style}|${opts.color ?? ''}|${opts.px ?? ''}`;
  let tex = _lines.get(key);
  if (!tex) {
    tex = paintLine(text, opts);
    tex.userData.shared = true;   // one copy for every ship: never disposed by one
    _lines.set(key, tex);
  }
  return tex;
}

function paintLine(text: string, opts: LineOpts): THREE.CanvasTexture {
  const { turn, style, px = 128 } = opts;
  const brush = style === 'brush';
  const color = opts.color ?? (brush ? '#8e2a1e' : '#dfe6f0');
  let seed = [...text].reduce((h, ch) => Math.imul(h ^ ch.charCodeAt(0), 16777619), 2166136261) >>> 0;
  const rand = (): number => {                          // mulberry32: each word paints the same every time
    seed = (seed + 0x6d2b79f5) >>> 0;
    let t = Math.imul(seed ^ (seed >>> 15), seed | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const font = brush ? `bold ${px}px Georgia, "Times New Roman", serif` : `bold ${px}px "Arial Narrow", Arial, sans-serif`;
  const narrow = brush ? 0.72 : 1;                      // rustic capitals are tall and narrow
  const j = brush ? 1 : 0;                              // how far the hand wanders
  const probe = document.createElement('canvas').getContext('2d')!;
  probe.font = font;
  const glyphs = [...text];
  const adv = glyphs.map((g) => (probe.measureText(g).width + px * 0.05) * narrow);
  const len = adv.reduce((a, b) => a + b, 0) + px * 0.2;
  const c = document.createElement('canvas');
  c.width = Math.ceil(px * 1.5);                        // across the line
  c.height = Math.ceil(len);                            // along it
  const ctx = c.getContext('2d')!;
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate(turn * Math.PI / 2);
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.font = font;
  let x = -len / 2 + px * 0.1;
  glyphs.forEach((g, i) => {
    ctx.save();
    ctx.translate(x, j * (rand() - 0.5) * px * 0.07);
    ctx.rotate(j * (rand() - 0.5) * 0.08);
    ctx.scale(narrow, 1);
    if (brush) {
      ctx.globalAlpha = 0.45;                           // a second, offset pass frays the edge
      ctx.fillText(g, (rand() - 0.5) * 4, (rand() - 0.5) * 4);
      ctx.globalAlpha = 1;
    }
    ctx.fillText(g, 0, 0);
    ctx.restore();
    x += adv[i] ?? 0;
  });
  // the brush running dry: streaks down and across each letter
  ctx.globalCompositeOperation = 'destination-out';
  ctx.strokeStyle = '#000';
  for (let i = 0; i < (brush ? len * 0.8 : 0); i++) {
    const down = rand() < 0.55;
    const sx = -len / 2 + rand() * len, sy = (rand() - 0.5) * px;
    const l = px * (0.12 + rand() * 0.45);
    ctx.globalAlpha = 0.25 + rand() * 0.55;
    ctx.lineWidth = 0.8 + rand() * 1.6;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(down ? sx + (rand() - 0.5) * 4 : sx + l, down ? sy + l : sy + (rand() - 0.5) * 4);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

// diagonal bands `band` wide every `pitch` across a 256 canvas
function paintStripes(ctx: CanvasRenderingContext2D, band: number, pitch: number): void {
  for (let x = -256; x < 512; x += pitch) {
    ctx.beginPath();
    ctx.moveTo(x, 256);
    ctx.lineTo(x + 256, 0);
    ctx.lineTo(x + 256 + band, 0);
    ctx.lineTo(x + band, 256);
    ctx.closePath();
    ctx.fill();
  }
}

export function hazardStripeTexture(colorA = '#d97a2b', colorB = '#14161a', worn = 0.5): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = colorB;
  ctx.fillRect(0, 0, 256, 256);
  ctx.fillStyle = colorA;
  paintStripes(ctx, 44, 88);
  if (worn > 0) {
    ctx.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 160 * worn; i++) {
      ctx.globalAlpha = 0.2 + Math.random() * 0.6;
      ctx.beginPath();
      ctx.arc(Math.random() * 256, Math.random() * 256, 1 + Math.random() * 3, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Hazard striping as an annulus framing a port: `inner` is the hole's share of
 *  the outer radius, and outside the ring is transparent. */
export function hazardRingTexture(inner: number, colorA = '#d97a2b', colorB = '#14161a'): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d')!;
  ctx.beginPath();
  ctx.arc(128, 128, 127, 0, Math.PI * 2);
  ctx.arc(128, 128, 127 * inner, 0, Math.PI * 2, true);
  ctx.clip();
  ctx.fillStyle = colorB;
  ctx.fillRect(0, 0, 256, 256);
  ctx.fillStyle = colorA;
  paintStripes(ctx, 22, 44);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

export function decalMaterial(map: THREE.Texture, opts: { emissive?: number; opacity?: number } = {}): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    map, transparent: true, alphaTest: 0.04, roughness: 0.62, metalness: 0.1,
    opacity: opts.opacity ?? 1,
    emissive: opts.emissive ?? 0x000000, emissiveMap: opts.emissive ? map : null, emissiveIntensity: opts.emissive ? 0.25 : 0,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2, depthWrite: false,
  });
}

/** A decal shell hugging a cylinder flank: radius+ε, arc degrees wide,
 * `height` along the cylinder axis (+Y — align like any hull cylinder). A
 * `bottomRadius` makes it a cone's, for a hull that tapers under it. */
export function curvedDecal(radius: number, height: number, arcDeg: number, centerDeg: number, tex: THREE.Texture, opts: { emissive?: number } = {}): THREE.Mesh {
  return new THREE.Mesh(curvedDecalGeometry(radius, height, arcDeg, centerDeg), decalMaterial(tex, opts));
}

export function curvedDecalGeometry(radius: number, height: number, arcDeg: number, centerDeg: number,
  bottomRadius = radius): THREE.CylinderGeometry {
  const arc = THREE.MathUtils.degToRad(arcDeg);
  const start = THREE.MathUtils.degToRad(centerDeg) - arc / 2;
  return new THREE.CylinderGeometry(radius + 0.015, bottomRadius + 0.015, height, Math.max(6, Math.round(arcDeg / 7)), 1, true, start, arc);
}
